import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import Checkbox from '@/components/checkbox'
import {FileUpload} from '@/components/file-upload'
import Input from '@/components/input'
import PhoneInput from '@/components/phone-input'
import RichText from '@/components/rich-text'
import Select from '@/components/select'
import {Text} from '@/components/text'
import Textarea from '@/components/textarea'
import useFormErrors from '@/hooks/use-form-errors'
import {useRecaptcha} from '@/hooks/use-recaptcha'
import {useGlobalStrings} from '@/providers/global-strings'
import {useGoogleReCaptcha} from '@/providers/google-recaptcha'
import {useLocale} from '@/providers/locale'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

import type {State, CustomError, Action, FieldProps, StateValue} from './interfaces'
import type {Props as RichTextProps} from '@/components/rich-text'
import type {CaptchaAction} from '@/hooks/use-recaptcha'
import type {CountryCode as PhoneCountryCode} from 'libphonenumber-js'

import {FieldType, FieldWidth, ActionType} from './interfaces'
import {validators} from './validators'

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const useValidateInputs = () => {
    return (state: State, fields: FieldProps[]) => {
        const errors: CustomError[] = []

        fields.forEach((field: FieldProps) => {
            return field.validators.forEach((name) => {
                !validators[name](state, field.key) &&
                    errors.push({
                        key: field.key as string,
                        value: field.errorMessage,
                    })
            })
        })

        return errors
    }
}

export interface DynamicFormProps<T = State> {
    fields: FieldProps[]
    submitLabel?: string
    title?: string
    titleAs?: 'h1' | 'h2' | 'h3'
    dataTestId?: string
    submissionErrorMessage?: string
    hasApiError?: boolean
    privacyDisclaimer?: RichTextProps['content']
    captchaAction?: CaptchaAction
    handleSubmit: (state: T) => Promise<void>
}

const errorSpacePlaceholder = <Text $variant="label-base">{'\u00a0'}</Text>

export function DynamicForm<T = State>({
    captchaAction,
    fields,
    title,
    titleAs = 'h3',
    handleSubmit,
    submitLabel,
    dataTestId,
    hasApiError,
    submissionErrorMessage,
    privacyDisclaimer,
}: DynamicFormProps<T>) {
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasSubmissionError, setHasSubmissionError] = React.useState(false)
    const initialState: State = {}
    const validateInputs = useValidateInputs()
    const globalStrings = useGlobalStrings()
    const currentLocale = useLocale()
    const captcha = useRecaptcha(captchaAction)
    const {loadReCaptcha} = useGoogleReCaptcha()

    fields.forEach((field: FieldProps) => {
        // Phone number fields have two values, country code prefix and phone number
        if (field.type === FieldType.Phone) {
            initialState[`${field.key}-phoneNumber`] = ''
            initialState[`${field.key}-countryCode`] =
                currentLocale.country.toUpperCase() as PhoneCountryCode
        }

        initialState[field.key] = ''
    })

    const [state, dispatch] = React.useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)

    const handleInputChange = <T extends StateValue>(
        key: keyof State,
        value: T,
        parentKey?: keyof State,
    ) => {
        loadReCaptcha()

        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })

        /**
         * Phone input has subfields (phoneNumber and countryCode). The error is
         * set on the parent field so we need to clear it there, rather than on the subfield
         */
        clearError(parentKey ?? key)
    }

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const errors = validateInputs(state, fields)

        if (errors.length) {
            return errors.forEach((error) => {
                setError(error.key, error.value)
            })
        }

        setIsLoading(true)

        setHasSubmissionError(false)

        try {
            if (captchaAction) {
                await captcha?.protectedCall(async () => {
                    await handleSubmit(state as T)
                })
            } else {
                await handleSubmit(state as T)
            }
        } catch (error) {
            setHasSubmissionError(true)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <FormAreaWrapper>
            {title && (
                <Text $variant="heading-base" forwardedAs={titleAs}>
                    {title}
                </Text>
            )}
            <Form onSubmit={handleFormSubmit} noValidate>
                {fields.map((field, i) => {
                    switch (field.type) {
                        case FieldType.Text:
                        case FieldType.Email:
                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <Input
                                        label={field.label}
                                        type={field.type}
                                        value={state[field.key] as string}
                                        placeholder={field?.placeholder}
                                        onChange={(value) => handleInputChange(field.key, value)}
                                        error={errors[field.key]}
                                        data-testid={`${dataTestId}-${field.key}`}
                                        required={field.validators.includes('isRequired')}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        case FieldType.Phone:
                            const phoneNumberKey = `${field.key}-phoneNumber`
                            const countryCodeKey = `${field.key}-countryCode`

                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <PhoneInput
                                        label={field.label}
                                        setPhoneNumber={(value) =>
                                            handleInputChange(phoneNumberKey, value, field.key)
                                        }
                                        phoneNumber={state[phoneNumberKey] as string}
                                        setCountryCode={(value) =>
                                            handleInputChange(countryCodeKey, value, field.key)
                                        }
                                        countryCode={state[countryCodeKey] as PhoneCountryCode}
                                        error={errors[field.key]}
                                        data-testid={`${dataTestId}-${field.key}`}
                                        required={field.validators.includes('isValidPhoneNumber')}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        case FieldType.Textarea:
                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <Textarea
                                        label={field.label}
                                        value={state[field.key] as string}
                                        placeholder={field?.placeholder}
                                        onChange={(value) => handleInputChange(field.key, value)}
                                        error={errors[field.key] ?? ''}
                                        data-testid={`${dataTestId}-${field.key}`}
                                        required={field.validators.includes('isRequired')}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        case FieldType.Select:
                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <Select
                                        label={field.label}
                                        options={field.options}
                                        placeholder={field?.placeholder}
                                        value={state[field.key] as string}
                                        onChange={(value) => handleInputChange(field.key, value)}
                                        error={errors[field.key]}
                                        data-testid={`${dataTestId}-${field.key}`}
                                        required={field.validators.includes('isRequired')}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        case FieldType.Checkbox:
                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <Checkbox
                                        label={field.label}
                                        checked={state[field.key] as boolean}
                                        onChange={(value) => handleInputChange(field.key, value)}
                                        error={errors[field.key]}
                                        data-testid={`${dataTestId}-${field.key}`}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        case FieldType.FileUpload:
                            return (
                                <FieldWrapper $width={field.width} key={field.key}>
                                    <FileUpload
                                        id={`${field.id}-${i}`}
                                        label={field.label}
                                        allowedFileTypes={field.allowedFileTypes}
                                        uploadText={field.uploadText}
                                        fileTypesText={field.fileTypesText}
                                        onChange={(value) => handleInputChange(field.key, value)}
                                        error={errors[field.key]}
                                        data-testid={`${dataTestId}-${field.key}`}
                                        required={field.validators.includes('isRequired')}
                                        fileSize={field.fileSize}
                                    />

                                    {!errors[field.key] && errorSpacePlaceholder}
                                </FieldWrapper>
                            )
                        default:
                            throw new Error('Invalid field type passed')
                    }
                })}
                <FieldWrapper $width={FieldWidth.Full}>
                    <StyledButton loading={isLoading} data-testid={`${dataTestId}-submit`}>
                        {submitLabel || globalStrings.submit}
                    </StyledButton>
                </FieldWrapper>

                {(hasApiError || hasSubmissionError) && (
                    <FieldWrapper $width={FieldWidth.Full}>
                        <Text $variant="label-base" $color="error" role="alert">
                            {submissionErrorMessage ?? globalStrings.form_submission_error}
                        </Text>
                    </FieldWrapper>
                )}

                {privacyDisclaimer && (
                    <LegalTextWrapper>
                        <FieldWrapper $width={FieldWidth.Full}>
                            <RichText content={privacyDisclaimer} $variant="label-base" />
                        </FieldWrapper>
                    </LegalTextWrapper>
                )}
            </Form>
        </FormAreaWrapper>
    )
}

const MOBILE_BREAKPOINT = 'md'

const FormAreaWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing24};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: ${tokens.spacing40};
    }
`

const LegalTextWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing8};
    margin-top: ${tokens.spacing12};

    ${mq.min(MOBILE_BREAKPOINT)} {
        margin-top: ${tokens.spacing24};
        grid-column: 1 / span 2;
    }
`

const Form = styled.form`
    display: grid;
    grid-gap: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-template-columns: 1fr 1fr;
    }
`

interface FieldWrapperProps {
    $width: FieldWidth
}

const FieldWrapper = styled.div<FieldWrapperProps>`
    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-column: ${(props) => (props.$width === FieldWidth.Full ? '1 / span 2' : 'auto')};
    }
`

const StyledButton = styled(Button)`
    width: 100%;
`
